import { FC, JSX, useState } from 'react';
import { Box, Chip, Grid, Typography } from '@mui/joy';
import { useMediaQuery } from '@mui/material';
import { Pagination } from '@consolidocs-ui/components';
import { CompanyType } from '@consolidocs-ui/types';
import CompanyCard from './CompanyCard';

interface CompanySectionProps {
  title: string;
  color: 'primary' | 'neutral';
  companies: CompanyType[];
  onEdit: (id?: string) => void;
  onDelete: (id?: string) => void;
  onRetryProvisioning?: (id?: string) => void;
  /** Organization whose provisioning retry is in flight. */
  retryingId?: string | null;
}

// Cards per page on desktop (two rows of three) — enough to keep scrolling
// short. The shared Pagination is desktop-only, so mobile shows the full list.
const PAGE_SIZE = 6;

export const CompanySection: FC<CompanySectionProps> = ({
  title,
  color,
  companies,
  onEdit,
  onDelete,
  onRetryProvisioning,
  retryingId,
}): JSX.Element => {
  const [page, setPage] = useState(1);
  const isDesktop = useMediaQuery('(min-width:900px)');

  const totalPages = Math.max(1, Math.ceil(companies.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleCompanies = isDesktop
    ? companies.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
    : companies;

  return (
    <Box className="companies-section">
      <Box className="companies-section__header">
        <Typography level="title-lg" className="companies-section__title">
          {title}
        </Typography>
        <Chip size="sm" variant="soft" color={color}>
          {companies.length}
        </Chip>
      </Box>

      <Grid container spacing={2}>
        {visibleCompanies.map((company) => (
          <Grid xs={12} sm={6} md={4} key={company.id} sx={{ display: 'flex' }}>
            <CompanyCard
              company={company}
              onEdit={onEdit}
              onDelete={onDelete}
              onRetryProvisioning={onRetryProvisioning}
              isRetrying={!!company.id && company.id === retryingId}
            />
          </Grid>
        ))}
      </Grid>

      {isDesktop && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Pagination
            page={currentPage}
            setPage={setPage}
            totalPages={totalPages}
          />
        </Box>
      )}
    </Box>
  );
};

export default CompanySection;
